import { useMemo, useState } from "react";
import PageHeader from "@/components/base/PageHeader";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import Card from "@/components/base/Card";
import { ErrorState, LoadingState } from "@/components/base/StateViews";
import { useAsync } from "@/hooks/useAsync";
import { getClasses } from "@/services/academicService";
import { getCycles, getSchoolYears } from "@/services/schoolService";
import { copyFiche, dossiersForClass, fetchFicheConfig, type LoadedFiche } from "@/services/ficheService";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/components/base/Toast";
import { formatMoney } from "@/utils/format";
import type { ClassFiche } from "@/types/fiche";
import type { Cycle, SchoolClass } from "@/types";
import ClassFicheModal from "./components/ClassFicheModal";
import DossierLibraryModal from "./components/DossierLibraryModal";
import EnrollmentFeesModal from "./components/EnrollmentFeesModal";
import ArticlesPeriodsModal from "./components/ArticlesPeriodsModal";
import FichePrintModal from "./components/FichePrintModal";

interface Loaded {
  config: LoadedFiche;
  classes: SchoolClass[];
  cycles: Cycle[];
  /** Année précédente (pour recopier sa fiche à la rentrée). */
  previousYear: string | null;
}

async function loadAll(): Promise<Loaded> {
  const [classes, cycles, years] = await Promise.all([getClasses(), getCycles(), getSchoolYears()]);
  const config = await fetchFicheConfig(classes);
  const labels = years.map((y) => y.label).sort();
  const previousYear = labels.filter((l) => l < config.header.schoolYear).pop() ?? null;
  return { config, classes, cycles, previousYear };
}

interface EditTarget {
  fiche: ClassFiche;
  classLabel: string;
  cycleLabel: string;
}

export default function FicheRenseignementsPage() {
  const { data, loading, error, reload } = useAsync<Loaded>(loadAll, []);
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [feesOpen, setFeesOpen] = useState(false);
  const [articlesOpen, setArticlesOpen] = useState(false);
  const [copying, setCopying] = useState(false);
  const { can } = useAuth();
  const { notify } = useToast();
  const canEdit = can("fees:update");

  const ficheByClass = useMemo(() => {
    const map: Record<string, ClassFiche> = {};
    (data?.config.classFiches ?? []).forEach((f) => {
      map[f.classId] = f;
    });
    return map;
  }, [data]);

  if (loading && !data) return <LoadingState label="Chargement de la fiche de renseignements..." />;
  if (error || !data) {
    return <ErrorState message={error ?? "Fiche introuvable."} onRetry={reload} />;
  }

  const { config, classes, cycles, previousYear } = data;
  const isEmpty = config.items.length === 0;

  const handleCopy = async () => {
    if (!previousYear) return;
    setCopying(true);
    try {
      await copyFiche(previousYear, config.header.schoolYear);
      notify(`Fiche ${previousYear} recopiée sur ${config.header.schoolYear} : ajustez les montants si besoin.`);
      reload();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Recopie impossible.", "error");
    } finally {
      setCopying(false);
    }
  };

  const cycleLabel = (cycleId: string) => cycles.find((c) => c.id === cycleId)?.label ?? "—";
  const totalTranches = config.classFiches.reduce((s, f) => s + f.tranches.length, 0);

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Fiche de renseignements"
        subtitle={`Frais d'inscription, tranches et dossiers par classe — ${config.header.schoolYear}`}
        icon="ri-file-text-line"
        actions={
          <>
            {canEdit && (
              <Button variant="outline" icon="ri-shopping-bag-3-line" onClick={() => setArticlesOpen(true)}>
                Articles &amp; périodes
              </Button>
            )}
            {canEdit && (
              <Button variant="outline" icon="ri-folder-settings-line" onClick={() => setLibraryOpen(true)}>
                Gérer les dossiers
              </Button>
            )}
            <Button icon="ri-printer-line" onClick={() => setPrintOpen(true)}>
              Imprimer / PDF
            </Button>
          </>
        }
      />
      {isEmpty && (
        <Card className="mb-6 border-accent-200 bg-accent-50/50">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-foreground-700">
              <i className="ri-information-line mr-1 text-accent-700"></i>
              Aucun frais n'est encore défini pour {config.header.schoolYear} : les élèves inscrits ne doivent rien.
              {previousYear ? ` Vous pouvez repartir de la fiche ${previousYear}.` : " Saisissez les frais ci-dessous."}
            </p>
            {previousYear && can("fees:create") && (
              <Button size="sm" icon="ri-file-copy-line" onClick={handleCopy} disabled={copying}>
                {copying ? "Recopie..." : `Recopier la fiche ${previousYear}`}
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Frais d'inscription / réinscription — saisis une seule fois */}
      <Card className="mb-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-700">
              <i className="ri-hand-coin-line text-xl"></i>
            </span>
            <div>
              <p className="font-semibold font-heading text-foreground-950">
                Frais d'inscription & réinscription
              </p>
              <p className="mt-0.5 text-xs text-foreground-500">
                Montants saisis une seule fois — valables pour toutes les classes, primaire et
                secondaire.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="rounded-md border border-background-200 bg-background-100/60 px-3 py-2">
              <p className="text-xs uppercase tracking-wide text-foreground-500">Inscription</p>
              <p className="text-sm font-semibold text-foreground-950">
                {formatMoney(config.enrollmentFees.inscription)}
              </p>
            </div>
            <div className="rounded-md border border-background-200 bg-background-100/60 px-3 py-2">
              <p className="text-xs uppercase tracking-wide text-foreground-500">Réinscription</p>
              <p className="text-sm font-semibold text-foreground-950">
                {formatMoney(config.enrollmentFees.reinscription)}
              </p>
            </div>
            {canEdit && (
              <Button variant="outline" size="sm" icon="ri-pencil-line" onClick={() => setFeesOpen(true)}>
                Modifier
              </Button>
            )}
          </div>
        </div>
      </Card>

      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Card className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-50 text-primary-700">
            <i className="ri-building-4-line text-xl"></i>
          </span>
          <div>
            <p className="text-xs text-foreground-500">Classes configurées</p>
            <p className="text-lg font-semibold font-heading text-foreground-950">
              {config.classFiches.filter((f) => f.tranches.length > 0).length}
            </p>
          </div>
        </Card>
        <Card className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-50 text-accent-700">
            <i className="ri-file-list-3-line text-xl"></i>
          </span>
          <div>
            <p className="text-xs text-foreground-500">Pièces au catalogue</p>
            <p className="text-lg font-semibold font-heading text-foreground-950">
              {config.dossiers.length}
            </p>
          </div>
        </Card>
        <Card className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
            <i className="ri-calendar-check-line text-xl"></i>
          </span>
          <div>
            <p className="text-xs text-foreground-500">Tranches définies</p>
            <p className="text-lg font-semibold font-heading text-foreground-950">{totalTranches}</p>
          </div>
        </Card>
      </div>

      {cycles.map((cycle) => {
        const cycleClasses = classes.filter((c) => c.cycleId === cycle.id);
        if (cycleClasses.length === 0) return null;
        const isPrimary = cycle.code === "PRIMAIRE";
        const dot = isPrimary ? "bg-warning-500" : "bg-secondary-500";
        const tone = isPrimary ? "warning" : "secondary";

        return (
          <section key={cycle.id} className="mb-8">
            <div className="mb-3 flex items-center gap-3">
              <span className={`h-2.5 w-2.5 rounded-full ${dot}`}></span>
              <h4 className="text-sm font-semibold font-heading text-foreground-900">
                Cycle {cycle.label}
              </h4>
              <Badge tone={tone}>{cycleClasses.length} classes</Badge>
            </div>

            <div className="overflow-hidden rounded-2xl border border-background-200 bg-background-50">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] border-collapse">
                  <thead>
                    <tr className="border-b border-background-200 bg-background-50/60 text-left text-xs uppercase tracking-wider text-foreground-500">
                      <th className="px-5 py-3 font-medium">Classe</th>
                      <th className="px-5 py-3 font-medium">Tranches (montants)</th>
                      <th className="px-5 py-3 text-right font-medium">Annuelle</th>
                      <th className="px-5 py-3 font-medium">Dossiers</th>
                      <th className="px-5 py-3 text-right font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cycleClasses.map((cls) => {
                      const fiche = ficheByClass[cls.id];
                      const dossiers = dossiersForClass(config, cls.id);
                      const total = fiche?.tranches.reduce((s, t) => s + t.amount, 0) ?? 0;
                      return (
                        <tr
                          key={cls.id}
                          className="border-b border-background-200 transition-colors last:border-0 hover:bg-background-100/60"
                        >
                          <td className="px-5 py-3.5">
                            <span className="text-sm font-medium text-foreground-900">{cls.name}</span>
                          </td>
                          <td className="px-5 py-3.5">
                            {fiche && fiche.tranches.length > 0 ? (
                              <div className="flex flex-wrap gap-1.5">
                                {fiche.tranches.map((t, i) => (
                                  <span
                                    key={t.id}
                                    className="inline-flex items-center gap-1 rounded-md border border-background-200 bg-background-100 px-2 py-0.5 text-xs text-foreground-700"
                                    title={t.dueDate ? `${t.label} — ${t.dueDate}` : t.label}
                                  >
                                    <span className="text-foreground-400">T{i + 1}</span>
                                    {t.amount > 0 ? formatMoney(t.amount) : <span className="text-accent-700">à saisir</span>}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-foreground-400">—</span>
                            )}
                          </td>
                          <td className="px-5 py-3.5 text-right text-sm font-semibold text-foreground-900 tabular-nums">
                            {formatMoney(total)}
                          </td>
                          <td className="px-5 py-3.5 text-sm text-foreground-700">
                            {dossiers.length > 0 ? (
                              <span
                                title={dossiers.map((d) => d.label).join(", ")}
                                className="inline-flex items-center gap-1 text-foreground-700"
                              >
                                <span className="font-semibold text-foreground-900">
                                  {dossiers.length}
                                </span>{" "}
                                pièces
                              </span>
                            ) : (
                              <span className="text-xs text-foreground-400">—</span>
                            )}
                          </td>
                          <td className="px-5 py-3.5">
                            <div className="flex items-center justify-end">
                              <button
                                type="button"
                                disabled={!fiche || !canEdit}
                                onClick={() =>
                                  fiche &&
                                  setEditTarget({
                                    fiche,
                                    classLabel: cls.name,
                                    cycleLabel: cycleLabel(cls.cycleId),
                                  })
                                }
                                aria-label={`Modifier ${cls.name}`}
                                className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-primary-50 hover:text-primary-700 disabled:opacity-40 disabled:hover:bg-transparent cursor-pointer"
                              >
                                <i className="ri-pencil-line"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        );
      })}

      <Card className="bg-background-100/50">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
            <i className="ri-information-line text-lg"></i>
          </span>
          <div className="text-sm text-foreground-700">
            <p className="font-medium text-foreground-900">Rappel des périodes de recouvrement</p>
            <ul className="mt-1.5 space-y-0.5 text-xs text-foreground-600">
              {config.periods.map((p) => (
                <li key={p.id}>
                  <span className="font-semibold text-foreground-800">{p.label}</span> : {p.value}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-foreground-500">
              Le bouton « Imprimer / PDF » génère le document complet sur une seule page, avec les
              dossiers, les frais d'écolage par classe et les mentions de l'école.
            </p>
          </div>
        </div>
      </Card>

      <ClassFicheModal
        open={editTarget !== null}
        onClose={() => setEditTarget(null)}
        fiche={editTarget?.fiche ?? null}
        classLabel={editTarget?.classLabel ?? ""}
        cycleLabel={editTarget?.cycleLabel ?? ""}
        dossiers={config.dossiers}
        fees={config.enrollmentFees}
        otherClasses={classes
          .filter((cls) => cls.id !== editTarget?.fiche.classId && ficheByClass[cls.id])
          .map((cls) => ({ fiche: ficheByClass[cls.id], label: cls.name, cycleLabel: cycleLabel(cls.cycleId) }))}
        onSaved={reload}
      />

      <EnrollmentFeesModal open={feesOpen} onClose={() => setFeesOpen(false)} fiche={config} onSaved={reload} />

      <DossierLibraryModal
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        dossiers={config.dossiers}
        onChanged={reload}
      />

      <ArticlesPeriodsModal
        open={articlesOpen}
        onClose={() => setArticlesOpen(false)}
        fiche={config}
        classes={classes}
        cycles={cycles}
        onChanged={reload}
      />

      <FichePrintModal
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        config={config}
        classes={classes}
        cycles={cycles}
      />
    </div>
  );
}